import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Boxes, Tag, Layers } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { inventoryAPI } from '../../services/api';
import AssetTabs from './AssetTabs';

const UNIT_OPTIONS = ['pcs', 'pairs', 'sets', 'boxes', 'litres', 'kg'];

export default function AddEditInventoryItem() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    item_name: '',
    category: '',
    make: '',
    colour: '',
    size: '',
    model: '',
    unit: 'pcs',
    quantity_on_hand: '0',
    reorder_threshold: '',
    location: '',
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        quantity_on_hand: parseFloat(form.quantity_on_hand) || 0,
        reorder_threshold: form.reorder_threshold ? parseFloat(form.reorder_threshold) : null,
      };
      const res = await inventoryAPI.create(payload);
      navigate(`/dashboard/assets/inventory/${res.data.id}`);
    } catch (err) {
      alert(err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'ga-input w-full px-4 py-2 rounded-lg';
  const labelCls = 'block text-sm font-medium text-ga-text-muted mb-1';
  const sectionCls = 'text-sm font-semibold uppercase tracking-wide text-ga-text-muted flex items-center gap-2';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/assets/inventory" aria-label="Back to inventory" className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-ga-text-primary">Add inventory item</h1>
        </div>

        <AssetTabs active="inventory" />

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Boxes className="h-4 w-4" />Item details</h2>
            <div>
              <label htmlFor="inv-name" className={labelCls}>Item name *</label>
              <input id="inv-name" name="item_name" value={form.item_name} onChange={handleChange} required className={inputCls} placeholder="e.g. Cricket balls" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="inv-category" className={labelCls}>Category</label>
                <input id="inv-category" name="category" value={form.category} onChange={handleChange} className={inputCls} placeholder="e.g. Training" />
              </div>
              <div>
                <label htmlFor="inv-unit" className={labelCls}>Unit</label>
                <select id="inv-unit" name="unit" value={form.unit} onChange={handleChange} className={inputCls}>
                  {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="inv-location" className={labelCls}>Storage location</label>
              <input id="inv-location" name="location" value={form.location} onChange={handleChange} className={inputCls} placeholder="e.g. Equipment shed, Shelf B" />
            </div>
          </section>

          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Tag className="h-4 w-4" />Attributes</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="inv-make" className={labelCls}>Make</label>
                <input id="inv-make" name="make" value={form.make} onChange={handleChange} className={inputCls} placeholder="e.g. SG, Nike" />
              </div>
              <div>
                <label htmlFor="inv-model" className={labelCls}>Model</label>
                <input id="inv-model" name="model" value={form.model} onChange={handleChange} className={inputCls} placeholder="e.g. Pro 500" />
              </div>
              <div>
                <label htmlFor="inv-colour" className={labelCls}>Colour</label>
                <input id="inv-colour" name="colour" value={form.colour} onChange={handleChange} className={inputCls} placeholder="e.g. White, Red/Blue" />
              </div>
              <div>
                <label htmlFor="inv-size" className={labelCls}>Size</label>
                <input id="inv-size" name="size" value={form.size} onChange={handleChange} className={inputCls} placeholder="e.g. Standard, M, 4-piece" />
              </div>
            </div>
          </section>

          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Layers className="h-4 w-4" />Stock</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="inv-qty" className={labelCls}>Quantity on hand</label>
                <input id="inv-qty" type="number" min="0" step="0.01" name="quantity_on_hand" value={form.quantity_on_hand} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="inv-threshold" className={labelCls}>Reorder threshold</label>
                <input id="inv-threshold" type="number" min="0" step="0.01" name="reorder_threshold" value={form.reorder_threshold} onChange={handleChange} className={inputCls} placeholder="Alert when at or below" />
              </div>
            </div>
            <div>
              <label htmlFor="inv-notes" className={labelCls}>Notes</label>
              <textarea id="inv-notes" name="notes" value={form.notes} onChange={handleChange} rows={3} className={inputCls} />
            </div>
          </section>

          <button type="submit" disabled={loading} className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg">
            <Save className="h-4 w-4" />
            {loading ? 'Saving…' : 'Create item'}
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
}
